import React, { useCallback, useEffect, useRef } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { Button, DialogActions, DialogContent } from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import FormikGrid from '../../../../common/formik/FormikGrid';
import FormikButton from '../../../../common/formik/FormikButton';
import FormikTextField from '../../../../common/formik/FormikTextField';
import { FileDto } from '../../Document.interface';
import { useCreateDocumentFolder } from '../../Document.hook';
import { DocumentFolderUploadComponentProps } from './DocumentFolderUpload';

export interface DocumentFolderUploadInputProps
  extends DocumentFolderUploadComponentProps {
  createOnMount?: boolean;
}

export const DocumentFolderUploadInput: React.FC<DocumentFolderUploadInputProps> = (
  props: DocumentFolderUploadInputProps
) => {
  const {
    content,
    actions,
    folderName,
    parentFolder,
    createOnMount = false,
    onLoad,
    onError,
    onSelect
  } = props;
  const formikRef = useRef<FormikHelpers<Partial<FileDto>>>();
  const [{ data, isLoading, error }, createFolder] = useCreateDocumentFolder();
  const isParentRoot: boolean = !parentFolder?.id;
  const parentId: number | undefined = parentFolder?.id || undefined;

  const doCreateFolder = useCallback(
    (folderName: string) => createFolder({ name: folderName, parentId }),
    [parentId, createFolder]
  );

  useEffect(() => {
    formikRef.current?.setSubmitting(isLoading);
    onLoad?.(isLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  useEffect(() => {
    // attempt to create folder if name is available
    if (folderName && createOnMount) {
      doCreateFolder(folderName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderName, doCreateFolder]);

  useEffect(() => {
    if (data) {
      onSelect?.(data, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (error) {
      onError?.(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <Formik
      innerRef={(formik: any) => (formikRef.current = formik)}
      initialValues={{ name: folderName || '' }}
      validationSchema={Yup.object({
        name: Yup.string().required('Folder name is required.')
      })}
      onSubmit={({ name }) => doCreateFolder(name)}
    >
      <Form autoComplete='off'>
        <DialogContent>
          <FormikGrid>
            <FormikTextField
              autoFocus
              type='text'
              name='name'
              label='Folder Name'
              disabled={isLoading}
            />
          </FormikGrid>
          <Alert severity='info'>
            {!folderName && 'No folder name detected.'} Specify the name of the
            folder or upload files to the{' '}
            {isParentRoot ? (
              <>
                <strong>root</strong> folder.
              </>
            ) : (
              <>
                current folder <strong>{parentFolder?.name || '?'}</strong>.
              </>
            )}
          </Alert>
        </DialogContent>
        {content}
        <DialogActions>
          {actions}
          <Button
            color='primary'
            variant='outlined'
            disabled={isLoading}
            onClick={() => onSelect?.(parentFolder, false)}
          >
            Upload to {isParentRoot ? 'root' : 'current'} Folder
          </Button>
          <FormikButton>Create Folder</FormikButton>
        </DialogActions>
      </Form>
    </Formik>
  );
};

export default DocumentFolderUploadInput;
